import { PlanningState, initialPlanningState } from '../types/planning';

import { initializeNarrativeState } from './narrativeHelpers';
import { validateNarrativeWorldState } from './worldStateValidation';

// Define validation rules for state fields
export interface ValidationRule<T> {
  field: keyof T;
  required?: boolean;
  type?: 'string' | 'number' | 'boolean' | 'object' | 'array';
  minLength?: number;
  maxLength?: number;
  validate?: (value: any) => boolean;
  sanitize?: (value: any) => any;
}

// Validation rules for planning state
export const planningStateRules: ValidationRule<PlanningState>[] = [
  { field: 'dynamicType', type: 'string' },
  { field: 'realConversation', type: 'string', maxLength: 10000 },
  { field: 'fictionalAnalysis', type: 'string', maxLength: 10000 },
  { field: 'playerRole', type: 'string', maxLength: 500 },
  { field: 'npcRole', type: 'string', maxLength: 500 },
  { field: 'playerName', type: 'string', maxLength: 100 },
  { field: 'npcName', type: 'string', maxLength: 100 },
  { field: 'playerMotivation', type: 'string', maxLength: 1000 },
  { field: 'npcMotivation', type: 'string', maxLength: 1000 },
  { field: 'aPlot', type: 'string', maxLength: 1000 },
  { field: 'stakes', type: 'string', maxLength: 1000 },
  { field: 'locationAtmosphere', type: 'string', maxLength: 1000 },
  { field: 'bPlotSeed', type: 'string', maxLength: 1000 },
  { field: 'characterNuanceNotes', type: 'string', maxLength: 2000 },
  { field: 'bPlotReason', type: 'string' },
  { field: 'bPlotReasonOther', type: 'string', maxLength: 500 },
  { field: 'bPlotNature', type: 'string' },
  { field: 'bPlotNatureOther', type: 'string', maxLength: 500 },
  { field: 'bPlotExplanation', type: 'string', maxLength: 2000 },
  { field: 'extraNotes', type: 'string', maxLength: 5000 },
];

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sanitizedData?: any;
}

// Generic validator function
export function validateObject<T>(
  obj: any,
  rules: ValidationRule<T>[],
  defaultValue: T
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const sanitizedData: any = { ...defaultValue };

  if (!obj || typeof obj !== 'object') {
    return {
      isValid: false,
      errors: ['Invalid object structure'],
      warnings: [],
      sanitizedData: defaultValue,
    };
  }

  for (const rule of rules) {
    const fieldName = String(rule.field);
    const value = obj[rule.field];

    try {
      // Check if required field is missing
      if (rule.required && (value === undefined || value === null || value === '')) {
        errors.push(`Field '${fieldName}' is required`);
        continue;
      }

      // Skip validation for optional empty fields
      if (!rule.required && (value === undefined || value === null)) {
        sanitizedData[rule.field] = defaultValue[rule.field];
        continue;
      }

      // Type validation
      if (rule.type && value !== undefined && value !== null) {
        const actualType = Array.isArray(value) ? 'array' : typeof value;
        if (actualType !== rule.type) {
          warnings.push(
            `Field '${fieldName}' expected type '${rule.type}' but got '${actualType}'`
          );
          // Try to coerce the type
          sanitizedData[rule.field] = coerceType(value, rule.type, defaultValue[rule.field]);
          continue;
        }
      }

      // Length validation for strings
      if (rule.type === 'string' && typeof value === 'string') {
        if (rule.minLength && value.length < rule.minLength) {
          warnings.push(`Field '${fieldName}' is shorter than minimum length ${rule.minLength}`);
        }
        if (rule.maxLength && value.length > rule.maxLength) {
          warnings.push(`Field '${fieldName}' exceeds maximum length ${rule.maxLength}`);
          // Truncate the value
          sanitizedData[rule.field] = value.substring(0, rule.maxLength);
          continue;
        }
      }

      // Custom validation
      if (rule.validate && !rule.validate(value)) {
        errors.push(`Field '${fieldName}' failed custom validation`);
        continue;
      }

      // Apply sanitization
      if (rule.sanitize) {
        sanitizedData[rule.field] = rule.sanitize(value);
      } else {
        sanitizedData[rule.field] = value;
      }
    } catch (error) {
      errors.push(`Error validating field '${fieldName}': ${error}`);
      sanitizedData[rule.field] = defaultValue[rule.field];
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    sanitizedData,
  };
}

// Type coercion helper
function coerceType(value: any, expectedType: string, fallback: any): any {
  try {
    switch (expectedType) {
      case 'string':
        return String(value);
      case 'number': {
        const num = Number(value);
        return isNaN(num) ? fallback : num;
      }
      case 'boolean':
        return Boolean(value);
      case 'object':
        return typeof value === 'object' ? value : fallback;
      case 'array':
        return Array.isArray(value) ? value : fallback;
      default:
        return fallback;
    }
  } catch {
    return fallback;
  }
}

// Specific validation functions
export function validatePlanningState(planningState: any): ValidationResult {
  return validateObject(planningState, planningStateRules, initialPlanningState);
}

export function validateNarrativeState(narrativeState: any): ValidationResult {
  if (!narrativeState || typeof narrativeState !== 'object') {
    return {
      isValid: false,
      errors: ['Invalid narrative state structure'],
      warnings: [],
      sanitizedData: initializeNarrativeState(),
    };
  }

  const errors: string[] = [];
  const warnings: string[] = [];

  // Check required fields
  if (!narrativeState.variables || typeof narrativeState.variables !== 'object') {
    errors.push('Missing or invalid variables object');
  }

  if (!narrativeState.nodes || typeof narrativeState.nodes !== 'object') {
    errors.push('Missing or invalid nodes object');
  }

  if (!narrativeState.currentNodeId || typeof narrativeState.currentNodeId !== 'string') {
    warnings.push('Missing or invalid currentNodeId');
  }

  if (!narrativeState.currentSegmentId) {
    warnings.push('Missing currentSegmentId');
  }

  if (!narrativeState.segmentCompletion || typeof narrativeState.segmentCompletion !== 'object') {
    warnings.push('Missing or invalid segmentCompletion');
  }

  if (narrativeState.nodes && typeof narrativeState.nodes === 'object') {
    Object.entries(narrativeState.nodes as Record<string, any>).forEach(([id, node]) => {
      if (!node || typeof node !== 'object') {
        errors.push(`Node '${id}' is invalid`);
        return;
      }
      if (!node.type) {
        errors.push(`Node '${id}' missing type`);
      }
      if (!node.segmentId) {
        errors.push(`Node '${id}' missing segmentId`);
      }
      if (node.type === 'Standard' && typeof node.text !== 'string') {
        warnings.push(`Node '${id}' missing text field`);
      }
    });
  }

  // Validate world-specific content
  const worldValidation = validateNarrativeWorldState(narrativeState);
  if (!worldValidation.isValid) {
    errors.push(...worldValidation.errors);
  }
  warnings.push(...worldValidation.warnings);

  const fallbackState = initializeNarrativeState();
  const sanitizedData = {
    variables: narrativeState.variables || fallbackState.variables,
    nodes: narrativeState.nodes || fallbackState.nodes,
    currentNodeId: narrativeState.currentNodeId || fallbackState.currentNodeId,
    currentSegmentId: narrativeState.currentSegmentId || fallbackState.currentSegmentId,
    segmentCompletion: narrativeState.segmentCompletion || fallbackState.segmentCompletion,
  };

  // Use world-validated nodes if available
  if (worldValidation.sanitized && worldValidation.sanitized.nodes) {
    sanitizedData.nodes = worldValidation.sanitized.nodes;
  }

  // Use world-validated variables if available
  if (worldValidation.sanitized && worldValidation.sanitized.variables) {
    sanitizedData.variables = worldValidation.sanitized.variables;
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    sanitizedData,
  };
}

// State recovery utility
export function recoverState<T>(
  rawState: any,
  validator: (state: any) => ValidationResult,
  label: string
): T {
  const validation = validator(rawState);

  if (validation.warnings.length > 0) {
    console.warn(`State validation warnings for ${label}:`, validation.warnings);
  }

  if (!validation.isValid) {
    console.error(`State validation errors for ${label}:`, validation.errors);
    console.warn(`Using fallback state for ${label}`);
  }

  return validation.sanitizedData as T;
}

// Comprehensive state health check
export function checkStateHealth(): {
  planningState: ValidationResult;
  narrativeState: ValidationResult;
  overall: boolean;
} {
  try {
    // Get current state from localStorage
    const planningData = localStorage.getItem('planningState');
    const appStoreData = localStorage.getItem('app-store');

    let planningResult: ValidationResult = { isValid: true, errors: [], warnings: [] };
    let narrativeResult: ValidationResult = { isValid: true, errors: [], warnings: [] };

    // Validate planning state
    if (planningData) {
      try {
        const parsedPlanning = JSON.parse(planningData);
        planningResult = validatePlanningState(parsedPlanning);
      } catch (error) {
        planningResult = {
          isValid: false,
          errors: [`Failed to parse planning state: ${error}`],
          warnings: [],
          sanitizedData: initialPlanningState,
        };
      }
    }

    // Validate narrative state from app store
    if (appStoreData) {
      try {
        const parsedStore = JSON.parse(appStoreData);
        if (parsedStore.state?.narrativeState) {
          narrativeResult = validateNarrativeState(parsedStore.state.narrativeState);
        }
      } catch (error) {
        narrativeResult = {
          isValid: false,
          errors: [`Failed to parse app store: ${error}`],
          warnings: [],
          sanitizedData: initializeNarrativeState(),
        };
      }
    }

    return {
      planningState: planningResult,
      narrativeState: narrativeResult,
      overall: planningResult.isValid && narrativeResult.isValid,
    };
  } catch (error) {
    console.error('Error during state health check:', error);
    return {
      planningState: { isValid: false, errors: [`Health check failed: ${error}`], warnings: [] },
      narrativeState: { isValid: false, errors: [`Health check failed: ${error}`], warnings: [] },
      overall: false,
    };
  }
}

// Emergency state repair function
export function repairCorruptedState(): void {
  console.warn('Attempting to repair corrupted application state...');

  try {
    const healthCheck = checkStateHealth();

    // Repair planning state if needed
    if (!healthCheck.planningState.isValid && healthCheck.planningState.sanitizedData) {
      localStorage.setItem(
        'planningState',
        JSON.stringify(healthCheck.planningState.sanitizedData)
      );
      console.log('Repaired planning state');
    }

    // Repair narrative state if needed
    if (!healthCheck.narrativeState.isValid && healthCheck.narrativeState.sanitizedData) {
      // This is more complex as it's part of the app store
      // We'll let the store initialization handle this through normal recovery mechanisms
      console.log('Narrative state will be repaired on next store initialization');
    }

    console.log('State repair completed');
  } catch (error) {
    console.error('Failed to repair state:', error);
    console.warn('Consider clearing localStorage to reset application state');
  }
}
